import React, {useEffect, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ActivityIndicator, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import BackButton from '../../components/rewards/BackButton';
import CountdownTimer from '../../components/rewards/CountdownTimer';
import RewardsProgramImage from '../../components/rewards/ProgramImage';
import RewardsCard from '../../components/rewards/RewardsCard';
import RewardsLink from '../../components/rewards/RewardsLink';
import StaffStorePicker from '../../components/StaffStorePicker';
import StoreItem from '../../components/StoreItem';
import Text from '../../components/themed/Text';
import Otp from '../../components/ui-controls/form/Otp';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../models/AppRoute';
import {Shop} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {VerifyPhoneStaffFailureReason} from '../../transport/types';
import {classifyStaffConflict, isStaffActivationBlocked} from '../../utils/staffActivation';
import {OtpVerificationFormData, otpVerificationSchema} from '../account/schemas/authSchemas';

import {styles} from './styles';
import {AccountStatus} from './types';

const RESEND_COOLDOWN_SECONDS = 30;
const RESEND_ERROR_MESSAGE = 'Could not resend code. Try again.';

// DIS-1673: fixed AC copy. `entered` is the six digits the user submitted,
// captured before the grid is cleared, so the code can be compared against the
// SMS without retyping it. The error Text below is an accessibilityRole="alert"
// live region, so these digits are also spoken by VoiceOver/TalkBack; the
// message is cleared the moment a new entry starts (see the effect below), so
// it never names a code the user has moved on from.
const wrongCodeMessage = (entered: string) =>
  `Incorrect code (${entered}). Check your text and re-enter.`;

// DIS-1190: surface specific phase-1 failure reasons inline. code_expired
// nudges the user to resend; max_attempts forces a fresh SMS; upstream/
// service hint at backend availability vs user error.
const getStaffVerifyErrorMessage = (
  reason: VerifyPhoneStaffFailureReason,
  entered: string,
): string => {
  switch (reason) {
    case 'code_invalid':
      return wrongCodeMessage(entered);
    case 'code_expired':
      return "Code expired. Tap 'Resend code' to get a new one.";
    case 'max_attempts':
      return "Too many attempts. Tap 'Resend code' to get a new one.";
    case 'phone_invalid':
      return 'That phone number looks invalid. Please go back and re-enter it.';
    case 'upstream':
    case 'service':
      return 'Service temporarily unavailable. Please try again shortly.';
    case 'network':
      return 'Connection issue. Please check your network and try again.';
    case 'unknown':
    default:
      return 'Something went wrong. Please try again.';
  }
};

const RewardsVerificationScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_VERIFICATION>) => {
    const {rewardsStore, userStore} = useStores();

    const [resending, setResending] = useState(false);
    const [codeResent, setCodeResent] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendError, setResendError] = useState<string | null>(null);
    // DIS-1308: change-store stays inline on this screen — navigating to
    // REWARDS_STAFF_STORE_SELECTION fired phase 3 (signUpStaff) before phase 1
    // had issued a sessionToken, yielding a 400 "sessionToken is required".
    const [isChangingStore, setIsChangingStore] = useState(false);
    // DIS-1673: the failure message CANNOT live in RHF's errors.verification.
    // Clearing the grid writes '' to Otp, which echoes onChangeText('') back
    // into field.onChange (Otp.tsx:114-117 + :121-128), re-running the resolver
    // and replacing any setError message with the zod length error. Screen
    // state is the only channel the echo cannot clobber.
    const [attemptError, setAttemptError] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    // Set synchronously before the first await. formState.isSubmitting updates
    // a render too late to stop a same-tick second entry, and onEnter (:445 on
    // the Otp element) bypasses the auto-submit effect entirely.
    const submitOnceRef = useRef(false);
    // The in-flight guard alone only stops a CONCURRENT second entry. onEnter
    // fired after the first attempt settles would resubmit the same digits, so
    // also remember what was last sent. Cleared by failAttempt, which is what
    // lets the user retry the very same code after a rejection.
    const lastSubmittedRef = useRef<string | null>(null);
    const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const mountedRef = useRef(true);
    // DIS-1135 bug_006: invalidate in-flight resend on blur (mountedRef
    // doesn't flip on blur with detachInactiveScreens, so a late resolve
    // would otherwise restart the cooldown / show "CODE RE-SENT" banner
    // when the user returns).
    const resendReqIdRef = useRef(0);
    const phoneNumber = rewardsStore.phoneNumber || '';
    const {flowType, returnReason} = route.params || {};

    // DIS-1185: staff recovery — staff signUp 400/500 sent the user back here
    // with returnReason set; staffStoreId + staffStore preserved on rewardsStore.
    const isStaffRecovery =
      rewardsStore.isStaffSignup &&
      returnReason === 'verification-expired' &&
      !!rewardsStore.staffStoreId &&
      !!rewardsStore.staffStore;

    // DIS-1236: staff full-signup (SIGNUP_LITE) already picked their store
    // on SignUp.tsx. Phase 3 can fire directly after phase 1 — no need to
    // visit REWARDS_STAFF_STORE_SELECTION. Existing-user staff flows
    // (NO_LITE_SIGNUP / ACTIVATE_REWARDS) skip SignUp.tsx so staffStoreId is
    // empty and the store-selection screen is still required for them.
    // Gate on both id AND full Shop: rewardsInflightPersist persists only
    // staffStoreId, so after a hard refresh staffStore is null. Falling
    // through to REWARDS_STAFF_STORE_SELECTION lets the user re-pick (their
    // phase-1 sessionToken is still valid for the 5min TTL) rather than
    // short-circuiting with no "YOUR SELECTED STORE" affordance.
    const isStaffNewSignupWithStore =
      rewardsStore.isStaffSignup &&
      flowType === AccountStatus.SIGNUP_LITE &&
      !!rewardsStore.staffStoreId &&
      !!rewardsStore.staffStore;

    const {control, handleSubmit, setValue, watch, reset} = useForm<OtpVerificationFormData>({
      resolver: zodResolver(otpVerificationSchema),
      defaultValues: {verification: ''},
      mode: 'onTouched',
    });

    // DIS-474: blur cleanup. Reset OTP form/cooldown/errors. DIS-1190
    // staff path is workflow-keyed by phone server-side; no client buffer.
    useFocusEffect(
      React.useCallback(() => {
        return () => {
          setResending(false);
          setCodeResent(false);
          setResendCooldown(0);
          setResendError(null);
          setIsChangingStore(false);
          setAttemptError(null);
          setIsVerifying(false);
          submitOnceRef.current = false;
          lastSubmittedRef.current = null;
          reset({verification: ''});
          if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
          }
          ++resendReqIdRef.current;
        };
      }, [reset]),
    );

    // DIS-1185: defensive — empty phoneNumber (e.g. page refresh wiped MobX) → SignIn.
    useFocusEffect(
      React.useCallback(() => {
        if (!rewardsStore.phoneNumber) {
          navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
        }
      }, [navigation, rewardsStore.phoneNumber, rewardsStore.signInRouteParams]),
    );

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (cooldownIntervalRef.current) {
          clearInterval(cooldownIntervalRef.current);
        }
      };
    }, []);

    const startCooldown = () => {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      cooldownIntervalRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    // DIS-1673: every retryable failure clears the grid. The message, not the
    // empty grid, carries the reason — and clearing is what gives the user a
    // retry path at all, since auto-submit fires on a CHANGE into a 6-char
    // value and the VERIFY button no longer exists.
    const failAttempt = (message: string) => {
      setAttemptError(message);
      lastSubmittedRef.current = null;
      setValue('verification', '');
    };

    const verificationValue = watch('verification');

    // DIS-1673: auto-submit once all 6 digits are present.
    // Dependency array is [verificationValue] ONLY. handleSubmit/onSubmit are
    // re-created every render; including either re-fires this effect on every
    // render while the value is still 6 chars, and the finally-release means
    // the guard cannot stop that loop.
    useEffect(() => {
      if (verificationValue?.length === 6) {
        void handleSubmit(onSubmit)();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verificationValue]);

    // DIS-1673: drop the failure message as soon as a new entry begins. The
    // message names the code that was rejected, so leaving it up while the user
    // retypes would caption fresh digits with a stale code. failAttempt clears
    // the grid to '', so the first digit of the retry is the transition here.
    useEffect(() => {
      if (verificationValue?.length) {
        setAttemptError(null);
      }
    }, [verificationValue]);

    const onSubmit = async (values: OtpVerificationFormData) => {
      // Guard lives here, not in the effect: onEnter (on the Otp element)
      // calls handleSubmit(onSubmit) directly and never touches the effect.
      // A stray Enter after auto-submit would otherwise burn a second Twilio
      // attempt and can trip max_attempts on the staged-staff path.
      if (submitOnceRef.current) return;
      if (lastSubmittedRef.current === values.verification) return;
      submitOnceRef.current = true;
      lastSubmittedRef.current = values.verification;
      setIsVerifying(true);
      setAttemptError(null);
      try {
        await runVerification(values);
      } finally {
        submitOnceRef.current = false;
        setIsVerifying(false);
      }
    };

    const runVerification = async (values: OtpVerificationFormData) => {
      // DIS-1190: staff path uses the two-phase verify-lite flow. Phase 1
      // verifies the SMS code with the backend (which parks a Temporal
      // workflow keyed on phone). Phase 3 runs at StaffStoreSelectionScreen
      // (or here on staff-recovery, since the store was already chosen).
      // DIS-1384 (R7): broadened from isStaffSignup to usesStagedVerification so
      // LMG-staff (isStaffSignup=false, isLmgStaffFlow=true) rides the same staged
      // verify rails as store-staff.
      if (rewardsStore.usesStagedVerification) {
        const verifyResult = await rewardsStore.verifyStaffPhone(values.verification);
        if (!verifyResult.ok) {
          // DIS-1673: every reason keeps its own typed message and still
          // clears, so retyping re-triggers auto-submit — without the VERIFY
          // button the user has no other way back.
          failAttempt(getStaffVerifyErrorMessage(verifyResult.reason, values.verification));
          return;
        }

        // DIS-1384 (R7): LMG staff always complete on the dedicated department/
        // state selection screen — route there after OTP, BEFORE the store-staff
        // inline-complete and store-selection branches below (isStaffRecovery and
        // isStaffNewSignupWithStore are both false for LMG, so control would
        // otherwise fall through to the store picker at the end of this block).
        if (rewardsStore.isLmgStaffFlow) {
          navigation.navigate(AppRoute.REWARDS_LMG_STAFF_SELECTION, {
            flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
          });
          return;
        }

        // DIS-1185 / DIS-1236: store already selected (recovery OR full-signup
        // SIGNUP_LITE). Skip REWARDS_STAFF_STORE_SELECTION and complete phase 3
        // directly so the user doesn't re-pick the store they already chose.
        if (isStaffRecovery || isStaffNewSignupWithStore) {
          const completeResult = await rewardsStore.signUpStaff(
            flowType || AccountStatus.NO_LITE_SIGNUP,
          );
          if (completeResult.ok) {
            // DIS-1329: staff promotion blocked (Zen↔Zoho identity mismatch) —
            // block the staff flow and divert to the terminal screen instead of
            // Success.
            if (isStaffActivationBlocked(completeResult.data)) {
              navigation.navigate(AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED, {
                conflictKind: classifyStaffConflict(completeResult.data.staffActivation?.reason),
              });
              return;
            }
            navigation.navigate(AppRoute.REWARDS_SUCCESS);
            return;
          }
          if (completeResult.reason === 'account_not_found') {
            // DIS-1328: 422 — no CRM contact for this phone. Terminal, no retry.
            navigation.navigate(AppRoute.REWARDS_ACCOUNT_NOT_FOUND);
            return;
          }
          if (completeResult.reason === 'session_expired') {
            // Workflow timed out between phases — start over from phase 1.
            failAttempt("Verification expired. Tap 'Resend code' to get a new one.");
            return;
          }
          if (completeResult.reason === 'invalid_store_id') {
            // Rare but real: store can be deactivated/delisted between SignUp
            // selection and phase 3 (Airtable sync lag, admin changes). Clear
            // the stale selection and route to the picker so the user can
            // choose another.
            rewardsStore.setStaffStoreId(null);
            rewardsStore.setStaffStore(null);
            navigation.navigate(AppRoute.REWARDS_STAFF_STORE_SELECTION, {
              flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
            });
            return;
          }
          failAttempt('Something went wrong. Please try again.');
          return;
        }

        navigation.navigate(AppRoute.REWARDS_STAFF_STORE_SELECTION, {
          flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
        });
        return;
      }

      const shouldLogMeInThenActivate =
        flowType === AccountStatus.ACTIVATE_REWARDS && !rewardsStore.isStaffSignup;

      const signUpResponse = await rewardsStore.signUp(
        values.verification,
        flowType || AccountStatus.NO_LITE_SIGNUP,
      );
      if (signUpResponse === 'success') {
        if (shouldLogMeInThenActivate) {
          userStore.setPostLoginReturnToScreen(AppRoute.ROOT_REWARDS_FLOW, {
            screen: AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS,
            params: {activateFlow: 'logMeIn'},
          });

          navigation.navigate(
            AppRoute.ROOT as never,
            {
              screen: AppRoute.MAIN,
              params: {screen: AppRoute.ACCOUNT_NOT_LOGGED_IN},
            } as never,
          );
        } else {
          navigation.navigate(AppRoute.REWARDS_SUCCESS);
        }
      } else if (signUpResponse === 'account_not_found') {
        // DIS-1328: 422 — no CRM contact for this phone. Terminal, no retry.
        navigation.navigate(AppRoute.REWARDS_ACCOUNT_NOT_FOUND);
      } else {
        // DIS-1673: /rewards/verification-lite returns an untyped 500 for a
        // wrong code, an expired code, max-attempts AND a provider failure
        // alike (see docs/DIS-1673/notes/middleware-contract-finding.md), so
        // the client cannot name the actual reason here. Not verified: the
        // relative frequency of the four causes. The AC copy is used because
        // the ticket scopes this screen's wrong-code path; DIS-1676 will supply
        // the discriminator that lets the other three speak for themselves.
        failAttempt(wrongCodeMessage(values.verification));
      }
    };

    const resendCode = async () => {
      if (!rewardsStore.phoneNumber) {
        navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
        return;
      }
      if (resending || resendCooldown > 0) return;
      const myReqId = ++resendReqIdRef.current;
      reset({verification: ''});
      setResendError(null);
      setResending(true);

      try {
        const result = await apiService.rewardsVerifyExistingUserPhoneNumber({
          phoneNumber: rewardsStore.phoneNumber,
        });
        if (!mountedRef.current || myReqId !== resendReqIdRef.current) return;

        const success = result?.status === 200;
        setCodeResent(success);
        if (success) {
          startCooldown();
        } else {
          setResendError(RESEND_ERROR_MESSAGE);
        }
      } catch (_e) {
        if (!mountedRef.current || myReqId !== resendReqIdRef.current) return;
        setCodeResent(false);
        setResendError(RESEND_ERROR_MESSAGE);
      } finally {
        if (mountedRef.current && myReqId === resendReqIdRef.current) {
          setResending(false);
        }
      }
    };

    const dayName = new Date().toLocaleString('en-AU', {weekday: 'long'}).toLowerCase();

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flexGrow: 1}}
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}>
        <ContentView parentStyle={{paddingBottom: 16}} style={{flex: 1}}>
          <BackButton
            onPress={() => {
              // DIS-1343: return to the original sign-in entry point with
              // staff/signUpCode preserved, instead of popping to a mid-flow
              // screen. Also fixes the dead button after a deep-link/refresh
              // where canGoBack() is false.
              navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
            }}
          />
          <RewardsProgramImage variant="password" />
          <Text variant="heading1" style={{marginTop: 20}}>
            {codeResent ? 'CODE RE-SENT' : "WE'VE SENT THE CODE"}
          </Text>

          {codeResent ? (
            <Text style={{marginBottom: 20}}>We have re-sent your code to your mobile.</Text>
          ) : null}

          {returnReason === 'verification-expired' ? (
            <RewardsCard
              variant="warning"
              padding="s3"
              style={{marginBottom: 16}}
              accessibilityLabel="The code you entered was incorrect. Re-enter the code below or request a new one."
              testID="verification-expired-banner">
              <Text
                variant="body2"
                color="textWarning"
                style={{fontWeight: '600'}}
                accessibilityRole="alert"
                accessibilityLiveRegion="polite">
                The code you entered was incorrect.
              </Text>
              <Text variant="body2" color="textWarning" style={{marginTop: 4}}>
                Re-enter the code below, or tap &quot;Resend code&quot; to request a new one.
              </Text>
            </RewardsCard>
          ) : null}

          {(isStaffRecovery || isStaffNewSignupWithStore) && rewardsStore.staffStore ? (
            <View style={{marginBottom: 16}} testID="staff-recovery-store-card">
              <Text variant="subheading">YOUR SELECTED STORE</Text>
              <RewardsCard variant="subtle" padding="s3">
                <StoreItem item={rewardsStore.staffStore} dayName={dayName} />
                <RewardsLink
                  onPress={() => setIsChangingStore(true)}
                  align="flex-start"
                  accessibilityLabel="Change selected store"
                  testID="change-store-button"
                  style={{marginTop: 8}}>
                  Change Store
                </RewardsLink>
              </RewardsCard>
              {isChangingStore ? (
                <StaffStorePicker
                  mode="compact"
                  selectedStore={null}
                  hideTrigger
                  autoOpenModal
                  onModalCancel={() => setIsChangingStore(false)}
                  onStoreSelected={(shop: Shop) => {
                    rewardsStore.setStaffStoreId(shop.store_number);
                    rewardsStore.setStaffStore(shop);
                    setIsChangingStore(false);
                  }}
                  testID="change-store-picker"
                />
              ) : null}
            </View>
          ) : null}

          {phoneNumber ? (
            <View style={{flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center'}}>
              <Text>Please verify your number by entering the code sent to {phoneNumber}. </Text>
              <RewardsLink
                onPress={() =>
                  navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams)
                }
                color="linkOnSurface"
                accessibilityLabel="Change phone number, return to sign in"
                testID="change-number-button">
                Change number
              </RewardsLink>
            </View>
          ) : null}

          <Text
            variant="body2"
            color={attemptError ? 'error' : undefined}
            style={{marginTop: 20, marginBottom: 8}}>
            Enter code:
          </Text>

          <Controller
            control={control}
            name="verification"
            render={({field: {onChange, onBlur, value}}) => (
              <Otp
                value={value}
                numInputs={6}
                onChangeText={onChange}
                onEnter={handleSubmit(onSubmit)}
                onBlur={onBlur}
                error={!!attemptError}
              />
            )}
          />

          <Text
            style={styles.errorMessage}
            accessibilityRole={attemptError ? 'alert' : undefined}
            accessibilityLiveRegion="polite"
            testID="verification-error">
            {attemptError ?? ' '}
          </Text>

          <RewardsCard variant="subtle" padding="s3">
            <View style={{flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
              <Text variant="body2" color="textMuted">
                {"Didn't get a code?"}
              </Text>
              {resending ? (
                <View
                  style={{flexDirection: 'row', alignItems: 'center', gap: 6}}
                  accessibilityRole="text"
                  accessibilityLiveRegion="polite"
                  testID="resend-sending-indicator">
                  <ActivityIndicator size="small" />
                  <Text variant="body2" color="textMuted">
                    Sending…
                  </Text>
                </View>
              ) : resendCooldown > 0 ? (
                <CountdownTimer
                  seconds={resendCooldown}
                  total={RESEND_COOLDOWN_SECONDS}
                  testID="resend-countdown-timer"
                />
              ) : (
                <RewardsLink
                  onPress={resendCode}
                  align="flex-start"
                  accessibilityLabel="Resend verification code"
                  testID="resend-code-button">
                  Resend code
                </RewardsLink>
              )}
            </View>
            {resendError ? (
              <Text
                variant="body2"
                color="error"
                style={{marginTop: 4}}
                accessibilityRole="alert"
                accessibilityLiveRegion="polite"
                testID="resend-error">
                {resendError}
              </Text>
            ) : null}
          </RewardsCard>

          {/* DIS-1673: the VERIFY button is gone — it was unreachable at large
              OS text sizes, which blocked sign-up entirely. Verification now
              fires on the 6th digit, so this row is the only in-flight
              affordance the user gets. */}
          {isVerifying ? (
            <View
              style={{marginTop: 32, flexDirection: 'row', alignItems: 'center', gap: 8}}
              accessibilityRole="text"
              accessibilityLiveRegion="polite"
              testID="verifying-indicator">
              <ActivityIndicator size="small" />
              <Text variant="body2" color="textMuted">
                Verifying…
              </Text>
            </View>
          ) : null}
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsVerificationScreen;
